import * as React from 'react';
import Tooltip from '@mui/material/Tooltip';
import { withStyles, WithStyles, createStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';

interface Props extends WithStyles<typeof styles> {
  readonly title: string;
  children?: React.ReactNode;
}

function arrowGenerator(color: string) {
  return {
    '&[x-placement*="bottom"] $arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.95em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent ${color} transparent`,
      },
    },
    '&[x-placement*="top"] $arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.95em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '1em 1em 0 1em',
        borderColor: `${color} transparent transparent transparent`,
      },
    },
    '&[x-placement*="right"] $arrow': {
      left: 0,
      marginLeft: '-0.95em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${color} transparent transparent`,
      },
    },
    '&[x-placement*="left"] $arrow': {
      right: 0,
      marginRight: '-0.95em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${color}`,
      },
    },
  };
}

const styles = (theme: Theme) =>
  createStyles({
    arrowPopper: arrowGenerator(theme.palette.grey[700]),
    arrow: {
      position: 'absolute',
      fontSize: 6,
      width: '3em',
      height: '3em',
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: 0,
        height: 0,
        borderStyle: 'solid',
      },
    },
    bootstrapPopper: arrowGenerator(theme.palette.common.black),
    bootstrapTooltip: {
      backgroundColor: theme.palette.common.black,
    },
    bootstrapPlacementLeft: {
      margin: '0 8px',
    },
    bootstrapPlacementRight: {
      margin: '0 8px',
    },
    bootstrapPlacementTop: {
      margin: '8px 0',
    },
    bootstrapPlacementBottom: {
      margin: '8px 0',
    },
    htmlPopper: arrowGenerator('#dadde9'),
    htmlTooltip: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
      '& b': {
        fontWeight: theme.typography.fontWeightMedium,
      },
    },
  });

  class CustomTooltip extends React.Component<Props> {
    state = {
      arrowRef: null as HTMLSpanElement | null,
    };
  
    handleArrowRef = (node: HTMLSpanElement) => {
      this.setState({
        arrowRef: node,
      });
    };
  
    render() {
      const { classes } = this.props;
  
      return (
        <Tooltip
          title={
            <React.Fragment>
              {this.props.title}
              <span className={classes.arrow} ref={this.handleArrowRef} />
            </React.Fragment>
          }
          classes={{ 
            tooltip: classes.bootstrapTooltip,
            popper: classes.bootstrapPopper,
            tooltipPlacementLeft: classes.bootstrapPlacementLeft,
            tooltipPlacementRight: classes.bootstrapPlacementRight,
            tooltipPlacementTop: classes.bootstrapPlacementTop,
            tooltipPlacementBottom: classes.bootstrapPlacementBottom, 
          }}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'arrow',
                  enabled: Boolean(this.state.arrowRef),
                  options: {
                    element: this.state.arrowRef,
                  },
                },
              ],
            },
          }}
        >
          <div>{this.props.children}</div>
        </Tooltip>
      );
    }
  }

export const BelfiusTooltip = withStyles(styles, { withTheme: true })(CustomTooltip);
